/**
 * External task system form component with Vuexy styling.
 * Provides form for creating and editing external task systems.
 */

// React Imports
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// MUI Imports
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

// Component Imports
import { TextField } from '@core/components/mui';
import { hitlService } from '../../services/hitlService';
import { useNotificationStore } from '../../store/notificationStore';

const SYSTEM_TYPES = ['ServiceNow', 'Jira', 'Azure DevOps', 'Custom'];

/**
 * External task system form component for creating and editing external task systems.
 */
export const ExternalTaskSystemForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { addNotification } = useNotificationStore();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [systemType, setSystemType] = useState('');
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [authenticationJson, setAuthenticationJson] = useState('');
  const [taskMappingJson, setTaskMappingJson] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (isEdit && id) {
      loadSystem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadSystem = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await hitlService.getExternalTaskSystem(id);
      setName(data.name);
      setDescription(data.description || '');
      setSystemType(data.systemType);
      setApiBaseUrl(data.apiBaseUrl);
      setAuthenticationJson(''); // Don't load auth JSON for security
      setTaskMappingJson(''); // Don't load mapping JSON for security
      setIsActive(data.isActive);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load external task system';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!systemType.trim()) {
      setError('System type is required');
      return;
    }

    if (!apiBaseUrl.trim()) {
      setError('API Base URL is required');
      return;
    }

    // Validate URL format
    try {
      new URL(apiBaseUrl.trim());
    } catch {
      setError('API Base URL must be a valid URL');
      return;
    }

    // Validate JSON fields if provided
    if (authenticationJson.trim()) {
      try {
        JSON.parse(authenticationJson.trim());
      } catch {
        setError('Authentication JSON must be valid JSON');
        return;
      }
    }

    if (taskMappingJson.trim()) {
      try {
        JSON.parse(taskMappingJson.trim());
      } catch {
        setError('Task mapping JSON must be valid JSON');
        return;
      }
    }

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim() || undefined,
        systemType: systemType.trim(),
        apiBaseUrl: apiBaseUrl.trim(),
        authenticationJson: authenticationJson.trim() || undefined,
        taskMappingJson: taskMappingJson.trim() || undefined,
        isActive,
      };

      if (isEdit && id) {
        // Update is not implemented in the API yet, so we'll just show a message
        addNotification('Update functionality not yet implemented', 'info');
        // await hitlService.updateExternalTaskSystem(id, data);
        // addNotification('External task system updated successfully', 'success');
      } else {
        await hitlService.createExternalTaskSystem(data);
        addNotification('External task system created successfully', 'success');
      }
      navigate('/external-task-systems');
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to save external task system';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 6 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/external-task-systems')}>
            Back
          </Button>
          <Typography variant="h4" component="h1" fontWeight={600}>
            {isEdit ? 'Edit External Task System' : 'Create External Task System'}
          </Typography>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 6 }}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          {/* Basic Information */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 4 }}>
                  Basic Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      fullWidth
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth required>
                      <InputLabel>System Type</InputLabel>
                      <Select value={systemType} label="System Type" onChange={(e) => setSystemType(e.target.value)}>
                        {SYSTEM_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="API Base URL"
                      value={apiBaseUrl}
                      onChange={(e) => setApiBaseUrl(e.target.value)}
                      required
                      fullWidth
                      helperText="The base URL of the external task system API (e.g., https://your-instance.service-now.com/api)"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Authentication Configuration */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 4 }}>
                  Authentication Configuration
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Authentication JSON"
                      value={authenticationJson}
                      onChange={(e) => setAuthenticationJson(e.target.value)}
                      multiline
                      rows={6}
                      fullWidth
                      helperText="JSON configuration for authentication (API keys, OAuth tokens, etc.). Example: { 'apiKey': 'your-key', 'username': 'user' }"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Field Mapping Configuration */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 4 }}>
                  Field Mapping Configuration
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Task Mapping JSON"
                      value={taskMappingJson}
                      onChange={(e) => setTaskMappingJson(e.target.value)}
                      multiline
                      rows={6}
                      fullWidth
                      helperText="JSON configuration for mapping internal fields to external system fields. Example: { 'title': 'summary', 'description': 'description', 'status': 'state' }"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Status */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 4 }}>
                  Status
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                      label="Active"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Form Actions */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="outlined" onClick={() => navigate('/external-task-systems')}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={saving}
              >
                {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
